import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import Lightbox from '../components/Lightbox';
import { fetchCollection, fetchLocalizedCollection } from '../lib/cms';

export default function HomePage() {
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [cmsPartners, setCmsPartners] = useState([]);
  const [campaignData, setCampaignData] = useState(null);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    // FAQ
    fetchLocalizedCollection('faq', 'da').then((data) => {
      if (data && data.length) {
        setFaqs(data.sort((a, b) => (a.sortering || 0) - (b.sortering || 0)));
      }
    });

    // Samarbejdspartnere fra CMS (samarbejdspartnere eller partnere_leverandoerer)
    fetchCollection('samarbejdspartnere').then((items) => {
      if (items && items.length) {
        const sorted = items.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
        setCmsPartners(
          sorted.map((item) => ({
            name: item.firmanavn,
            img: Array.isArray(item.logo) ? item.logo[0] : item.logo,
            url: item.hjemmeside,
          }))
        );
      } else {
        // Fallback til partnere_leverandoerer
        fetchCollection('partnere_leverandoerer').then((pItems) => {
          if (pItems && pItems.length) {
            const sorted = pItems.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
            setCmsPartners(
              sorted.map((item) => ({
                name: item.navn,
                img: Array.isArray(item.logo) ? item.logo[0] : item.logo,
                url: item.hjemmeside,
              }))
            );
          }
        });
      }
    });

    // Kampagneblok fra CMS
    fetchCollection('kampagne').then((items) => {
      if (items && items.length) {
        const active = items.find((c) => c.aktiv !== false) || items[0];
        if (active) setCampaignData(active);
      }
    });

    // Forside sideredigering fra CMS
    fetchLocalizedCollection('sider', 'da').then((items) => {
      const getSlug = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || Object.values(it.slug)[0]
          : it?.slug;
      const match = items.find((s) => getSlug(s) === 'forside');
      if (match) setPageData(match);
    });
  }, []);

  const defaultFaqs = [
    {
      id: '1',
      spoergsmaal: 'Hvilken garanti yder PMB på maskiner og centralsmøring?',
      svar: '<p>Ved køb af nye maskiner, produkter og reservedele ydes 12 måneders garanti ved normal drift på maks. 8 timer dagligt. På nymonterede centralsmøringsanlæg yder vi to års garanti. På renoverede brugte maskiner ydes ligeledes 12 måneders garanti, mens øvrige brugte maskiner leveres med 3 måneders garanti.</p>',
    },
    {
      id: '2',
      spoergsmaal: 'Hvorfor vælge centralsmøring fra SKF og Lincoln?',
      svar: '<p>Centralsmøring sikrer ensartet og præcis dosering af smøremiddel, mindsker slitage på lejer og bøsninger, reducerer dyr nedetid og sparer op til 40% smøremiddel. Vores serviceteam kan desuden rykke ud i hele landet med kort responstid.</p>',
    },
    {
      id: '3',
      spoergsmaal: 'Kan en brugt presse tilpasses vores produktion?',
      svar: '<p>Ja, hver maskine gennemgås og testes efter strenge standarder. Vi kan opgradere maskinen med nyt udstyr, styringer eller overfladebehandling, så den passer præcist til dine krav, og levere den monteret og klar til drift.</p>',
    },
    {
      id: '4',
      spoergsmaal: 'Hvor hurtigt kan PMB yde teknisk support og service?',
      svar: '<p>PMB yder landsdækkende service og teknisk support. Vores serviceteam kan ofte være på pladsen inden for et døgn, og vi råder over et omfattende lager af reservedele, pumper og smøredele.</p>',
    },
  ];

  // Standard partnere fallback
  const defaultPartners = [
    {
      name: 'Fredko Group',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fredko-final-1.webp',
      url: 'https://www.fredko.com/',
    },
    {
      name: 'PM Service',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pm-service-final-1.webp',
      url: 'https://pmservice.se/',
    },
    {
      name: 'MVD Inan',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd.webp',
      url: 'https://www.mvd.com.tr/',
    },
    {
      name: 'Hidroliksan',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/hidroliksan-1.webp',
      url: 'https://www.hidroliksan.com/',
    },
    {
      name: 'SKF',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/skf.webp',
      url: 'https://www.skf.com/',
    },
    {
      name: 'Dimeco',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/dimeco.png',
      url: 'https://www.dimeco.com/',
    },
  ];

  // 6 symmetriske kort til "Primære produkter & ydelser" (3x2 grid på desktop)
  const primarySixCards = [
    {
      id: 'presser',
      title: 'Presser',
      desc: 'Hydrauliske presser, excenterpresser og højhastighedspresser fra førende europæiske producenter.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/presser.png',
      link: '/presser',
      clickable: true,
    },
    {
      id: 'centralsmoering',
      title: 'Centralsmøring',
      desc: 'Autoriseret forhandler af SKF og Lincoln Centralsmøring. Optimal drift og forlængelse af maskinernes levetid.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/centralsmoering.png',
      link: '/centralsmoering',
      clickable: true,
    },
    {
      id: 'mvd',
      title: 'MVD produkter',
      desc: 'Højpræcisions fiberlaserskæremaskiner, guillotinesakse og kantbukkere (servo og hydrauliske).',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/bearbejdning.png',
      link: '/laser',
      clickable: true,
    },
    {
      id: 'service',
      title: 'Service & Montage',
      desc: 'Landsdækkende teknisk service, reparation, opgradering og lovpligtige sikkerhedseftersyn af presser.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/service.png',
      link: '/service',
      clickable: true,
    },
    {
      id: 'brugte-maskiner',
      title: 'Brugte maskiner',
      desc: 'Gennemgåede og renoverede brugte maskiner med garanti, klar til drift og tilpasset jeres produktion.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/brugte-maskiner.jpg',
      link: '/brugte-maskiner',
      clickable: true,
    },
    {
      id: 'styringer-software',
      title: 'Styringer og software',
      desc: 'Moderne styringer og brugervenlig software til optimering af produktion, præcision og cyklustider.',
      img: null, // Ingen billedfil leveret til styringer og software endnu
      link: null,
      clickable: false,
    },
  ];

  // Kampagnedata (første kampagne ifølge spec)
  const defaultCampaign = {
    overskrift: 'START 2027 FULDELEKTRISK',
    undertitel: 'Fuldelektrisk Kantbukker · Varenr. 869E1003100',
    broedtekst:
      '<p>iBend fra MVD er en fuldelektrisk, remtrukket servo-kantbukker med to synkrone servomotorer. Der er ingen hydraulik og ingen olie, og den er markant mere støjsvag.</p><p>Bukkeprocesserne bliver op til 30 % kortere, og olieskift og lækager hører fortiden til.</p><p>Start 2027 med den teknologi, der bliver standarden fremover.</p>',
    fordeleList: [
      'Kort cyklustid',
      'Høj produktivitet',
      'Mindre energi',
      'Mindre støj',
      'Ingen olie',
    ],
    prisbudskab: 'Fås fra 429.100,00 kr.',
    cta_tekst: 'Se maskinen her',
    cta_link: '/produkt/mvd-e-series',
    produktbillede:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd-e-series.webp',
    producent_logo:
      'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/mvd.webp',
  };

  const campaign = campaignData || defaultCampaign;
  const campaignImg = Array.isArray(campaign.produktbillede)
    ? campaign.produktbillede[0]
    : campaign.produktbillede || defaultCampaign.produktbillede;
  const campaignLogo = Array.isArray(campaign.producent_logo)
    ? campaign.producent_logo[0]
    : campaign.producent_logo || defaultCampaign.producent_logo;

  const referencePartners = cmsPartners.length > 0 ? cmsPartners : defaultPartners;

  const heroImage =
    pageData && pageData.billede
      ? Array.isArray(pageData.billede)
        ? pageData.billede[0]
        : pageData.billede
      : 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fremforing-hero.webp';

  return (
    <>
      <Helmet>
        <title>{pageData?.seo_titel || 'POVL MØLLERS MASKINFABRIK A/S'}</title>
        <meta
          name="description"
          content={
            pageData?.meta_beskrivelse ||
            'POVL MØLLERS MASKINFABRIK A/S i Borup tilbyder professionel produktion og maskinfabrikation med fokus på kvalitet og præcision. Kontakt os for mere information.'
          }
        />
        <meta property="og:title" content={pageData?.seo_titel || 'POVL MØLLERS MASKINFABRIK A/S'} />
        <meta
          property="og:description"
          content={
            pageData?.intro ||
            'Maskiner - Smøring - Service – siden 1921. Presser, centralsmøring og industriel pladebearbejdning.'
          }
        />
        <meta property="og:image" content={heroImage} />
      </Helmet>

      {/* ==================================================
          1. FORSIDE – HERO / ØVERSTE SEKTION
          ================================================== */}
      <section className="relative bg-[#121619] text-white overflow-hidden">
        {/* Background Image / forberedt til karrusel eller video */}
        <div className="absolute inset-0 z-0">
          <CmsImage
            src={heroImage}
            alt="POVL MØLLERS MASKINFABRIK produktion"
            className="w-full h-full object-cover object-center opacity-65 filter brightness-95"
            width={1920}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#121619]/85 via-[#121619]/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl">
            {/* Main H1 Headline: "Maskiner - Smøring - Service" på ÉN samlet linje på desktop */}
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.15]">
              <span className="block whitespace-normal lg:whitespace-nowrap">
                Maskiner - Smøring - Service
              </span>
              <span className="text-[#0d8086] font-normal text-2xl sm:text-3xl lg:text-4xl block mt-2">
                – siden 1921
              </span>
            </h1>

            {/* Subtitle / brødtekst under overskriften: ensartet venstre/højre margin, visuelt balanceret */}
            <p className="mt-6 text-lg sm:text-xl text-stone-300 leading-relaxed max-w-2xl text-left">
              {pageData?.intro ||
                'Når en maskine står stille, står produktionen stille. Derfor handler vores arbejde om mere end maskiner og smøring. Hos os får du ikke bare en maskine eller en serviceaftale – du får en samarbejdspartner, der tager ansvar for, at din produktion bliver ved med at køre.'}
            </p>

            {/* CTAs: "Se vores maskinprogram" linker til "/produkter" */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt"
                className="px-7 py-4 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-lg flex items-center gap-2.5"
              >
                <span>Kontakt vores specialister</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
              <Link
                to="/produkter"
                className="px-7 py-4 rounded bg-white/10 hover:bg-white/20 text-white font-semibold text-sm tracking-wide border border-white/20 transition flex items-center gap-2"
              >
                <span>Se vores maskinprogram</span>
              </Link>
            </div>

            {/* Trust metric block */}
            <div className="mt-14 pt-8 border-t border-white/15 flex items-center gap-6">
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  7000+
                </span>
                <p className="text-xs uppercase tracking-wider text-stone-400 mt-0.5">
                  Virksomheder hjulpet
                </p>
              </div>
              <div className="h-10 w-px bg-white/20" />
              <div>
                <span className="text-3xl sm:text-4xl font-extrabold text-[#0d8086] tracking-tight">
                  100+ år
                </span>
                <p className="text-xs uppercase tracking-wider text-stone-400 mt-0.5">
                  Erfaring og kompetence
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================
          2. FORSIDE – "VORES FUNDAMENT"
          ================================================== */}
      <section className="py-20 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
                Vores fundament
              </span>

              {/* Overskrift */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight leading-snug">
                Jeres totalleverandør inden for metalbearbejdning
              </h2>

              {/* Præcis angivet brødtekst */}
              <div className="text-stone-700 space-y-4 text-base sm:text-lg leading-relaxed">
                <p>
                  Vi har allerede hjulpet mere end 7000 virksomheder med stabil drift og skarp præcision gennem maskiner fra nogle af Europas største producenter, herunder MVD, Hidroliksan og Bruderer.
                </p>
                <p>
                  Som kunde hos os er vi med hele vejen: fra vi finder den rigtige maskine til jeres behov, til den står klar hos jer, og jeres medarbejdere er trygge ved at bruge den. Vi leverer maskinen, sørger for reservedelene og står for det lovpligtige sikkerhedseftersyn.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm font-semibold text-stone-800">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Teknisk rådgivning</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Kundetilpassede anlæg</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-[#006368]" />
                  <span>Hurtig support</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white p-8 rounded-lg border border-stone-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full bg-[#006368]" />
                <h3 className="text-lg font-bold text-stone-900">Spørg os – vi er lige her</h3>
                <p className="mt-3 text-stone-600 text-sm leading-relaxed">
                  Har du brug for teknisk indsigt, sparring på dit projekt eller et hurtigt råd?
                  Vores specialister er kun ét opkald væk – og vi svarer, før du når at vente.
                </p>

                <div className="mt-6 pt-6 border-t border-stone-100 space-y-3">
                  <a
                    href="tel:57526262"
                    className="flex items-center gap-3 text-[#006368] font-bold text-base hover:text-[#004d51] transition"
                  >
                    <span className="w-10 h-10 rounded bg-[#006368]/10 flex items-center justify-center">
                      <i className="fa-solid fa-phone" />
                    </span>
                    <span className="whitespace-nowrap">Ring +45 57 52 62 62</span>
                  </a>
                  <a
                    href="mailto:info@pmborup.dk"
                    className="flex items-center gap-3 text-stone-700 hover:text-stone-900 text-sm font-medium transition"
                  >
                    <span className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center">
                      <i className="fa-solid fa-envelope" />
                    </span>
                    <span className="whitespace-nowrap">info@pmborup.dk</span>
                  </a>
                </div>

                <div className="mt-6">
                  <Link
                    to="/kontakt"
                    className="w-full text-center block px-5 py-3 rounded bg-[#006368] text-white text-sm font-semibold hover:bg-[#004d51] transition"
                  >
                    Kontakt os her
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================
          3. FORSIDE – "PRIMÆRE PRODUKTER & YDELSER"
          Undertitel placeret DIREKTE UNDER overskriften.
          Grid udvidet til 6 felter/kort (3 kolonner x 2 rækker).
          ================================================== */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header blok: undertitlen står DIREKTE UNDER overskriften */}
          <div className="mb-16 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#006368]">
              Vores ekspertise
            </span>
            <h2 className="text-2xl sm:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Primære produkter & ydelser
            </h2>
            <p className="text-stone-600 mt-3 text-sm sm:text-base leading-relaxed">
              Vi leverer totalløsninger fra enkeltstående kvalitetsmaskiner til komplette industrialiserede produktionslinjer og landsdækkende service.
            </p>
          </div>

          {/* Symmetrisk grid: 3 kolonner x 2 rækker på desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
            {primarySixCards.map((card) => {
              if (card.clickable && card.link) {
                return (
                  <Link
                    key={card.id}
                    to={card.link}
                    className="w-full group flex flex-col bg-stone-50 rounded-lg border border-stone-200 overflow-hidden hover:border-[#006368] hover:shadow-md transition duration-200 text-center items-center"
                  >
                    <div className="relative h-60 w-full overflow-hidden bg-white p-6 flex items-center justify-center border-b border-stone-200">
                      <CmsImage
                        src={card.img}
                        alt={card.title}
                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300"
                        width={500}
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col justify-between items-center w-full text-center">
                      <div className="w-full flex flex-col items-center">
                        <h3 className="text-xl font-bold text-stone-900 group-hover:text-[#006368] transition-colors text-center">
                          {card.title}
                        </h3>
                        <p className="mt-2.5 text-sm text-stone-600 leading-relaxed text-center">
                          {card.desc}
                        </p>
                      </div>
                      <div className="mt-6 pt-4 border-t border-stone-200/80 flex items-center justify-center gap-2 text-sm font-semibold text-[#006368] group-hover:text-[#004f53] w-full">
                        <span>Læs mere</span>
                        <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              }

              // Ikke-klikbart kort (fx "Styringer og software")
              return (
                <div
                  key={card.id}
                  className="w-full flex flex-col bg-stone-50 rounded-lg border border-stone-200 overflow-hidden text-center items-center"
                >
                  <div className="relative h-60 w-full overflow-hidden bg-white p-6 flex flex-col items-center justify-center border-b border-stone-200">
                    <div className="w-16 h-16 rounded-full bg-[#006368]/10 flex items-center justify-center text-[#006368] text-2xl mb-3">
                      <i className="fa-solid fa-microchip" />
                    </div>
                    <span className="text-xs uppercase tracking-wider font-semibold text-stone-400">
                      Teknisk integration
                    </span>
                  </div>
                  <div className="p-6 flex-1 flex flex-col justify-between items-center w-full text-center">
                    <div className="w-full flex flex-col items-center">
                      <h3 className="text-xl font-bold text-stone-900 text-center">
                        {card.title}
                      </h3>
                      <p className="mt-2.5 text-sm text-stone-600 leading-relaxed text-center">
                        {card.desc}
                      </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-stone-200/80 flex items-center justify-center gap-2 text-xs font-medium text-stone-400 w-full">
                      <span>Rådgivning & kundetilpasning</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================================================
          4. FORSIDE – PRODUKT-/KAMPAGNEBLOK
          Fleksibel kampagneblok administrerbar fra CMS.
          Første kampagne: "START 2027 FULDELEKTRISK" (varenr. 869E1003100)
          ================================================== */}
      <section className="py-20 sm:py-28 bg-[#121619] text-white relative border-y border-stone-800 overflow-hidden">
        {/* Subtil baggrundsglød */}
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#006368]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#0d8086]/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Top badge & overskrift */}
          <div className="mb-12">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-bolt text-[10px]" />
                <span>Månedens kampagne</span>
              </span>
              {campaign.varenummer && (
                <span className="text-xs text-stone-400 uppercase tracking-widest font-mono">
                  Varenr. {campaign.varenummer}
                </span>
              )}
              {campaign.undertitel && (
                <span className="text-xs text-[#0d8086] font-semibold">
                  · {campaign.undertitel}
                </span>
              )}
            </div>

            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight uppercase leading-tight">
              {campaign.overskrift || 'START 2027 FULDELEKTRISK'}
            </h2>
          </div>

          {/* Grid layout: Tekst/fordele i venstre og center, stort dominerende produktbillede til højre */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Venstre kolonne: Brødtekst, fordele, pris & CTA */}
            <div className="lg:col-span-6 space-y-8">
              {/* Brødtekst */}
              {campaign.broedtekst ? (
                <div
                  dangerouslySetInnerHTML={{ __html: campaign.broedtekst }}
                  className="text-stone-300 space-y-4 text-base sm:text-lg leading-relaxed [&_p]:mb-3 [&_strong]:text-white"
                />
              ) : (
                <div className="text-stone-300 space-y-4 text-base sm:text-lg leading-relaxed">
                  <p>
                    iBend fra MVD er en fuldelektrisk, remtrukket servo-kantbukker med to synkrone servomotorer. Der er ingen hydraulik og ingen olie, og den er markant mere støjsvag.
                  </p>
                  <p>
                    Bukkeprocesserne bliver op til 30 % kortere, og olieskift og lækager hører fortiden til.
                  </p>
                  <p className="font-semibold text-white">
                    Start 2027 med den teknologi, der bliver standarden fremover.
                  </p>
                </div>
              )}

              {/* Fremhævede fordele */}
              <div className="p-6 rounded-lg bg-stone-900/80 border border-stone-800">
                <h4 className="text-xs uppercase tracking-widest font-bold text-[#0d8086] mb-4">
                  Fremhævede fordele
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(campaign.fordeleList || [
                    'Kort cyklustid',
                    'Høj produktivitet',
                    'Mindre energi',
                    'Mindre støj',
                    'Ingen olie',
                  ]).map((fordel, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-stone-200">
                      <i className="fa-solid fa-circle-check text-[#0d8086] text-xs shrink-0" />
                      <span className="font-medium">{fordel}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Prisbudskab og CTA nederst */}
              <div className="pt-2 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                {campaign.prisbudskab && (
                  <div>
                    <span className="text-xs uppercase tracking-wider text-stone-400 block">
                      Kampagnepris
                    </span>
                    <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                      {campaign.prisbudskab}
                    </span>
                  </div>
                )}
                <Link
                  to={campaign.cta_link || '/produkt/mvd-e-series'}
                  className="px-8 py-4 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm tracking-wide transition shadow-xl flex items-center gap-3"
                >
                  <span>{campaign.cta_tekst || 'Se maskinen her'}</span>
                  <i className="fa-solid fa-arrow-right text-xs" />
                </Link>
              </div>
            </div>

            {/* Højre kolonne: Stort dominerende produktbillede */}
            <div className="lg:col-span-6 flex flex-col items-center">
              <div className="relative w-full rounded-xl bg-gradient-to-b from-stone-900 to-stone-950 p-6 sm:p-10 border border-stone-800 shadow-2xl overflow-hidden group">
                {campaignLogo && (
                  <div className="absolute top-6 left-6 z-10 bg-white/90 p-2.5 rounded shadow">
                    <img
                      src={campaignLogo}
                      alt="Producent logo"
                      className="h-6 w-auto object-contain"
                    />
                  </div>
                )}

                <div className="relative min-h-[320px] sm:min-h-[420px] flex items-center justify-center">
                  <CmsImage
                    src={campaignImg}
                    alt={campaign.overskrift || 'MVD iBend Fuldelektrisk Kantbukker'}
                    className="max-h-[380px] sm:max-h-[460px] w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                    width={900}
                  />
                </div>

                <div className="mt-4 pt-4 border-t border-stone-800/80 flex items-center justify-between text-xs text-stone-400">
                  <span className="font-mono">MVD E Series · iBend Servo</span>
                  <span className="text-[#0d8086] font-semibold">100% Elektrisk drift</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================
          5. FORSIDE – SAMARBEJDSPARTNERE
          Erstattet fra "Dokumenteret Tillid" (kundelogoer)
          til virksomhedens samarbejdspartnere.
          Logoerne kan vedligeholdes i CMS'et (samarbejdspartnere).
          Hvert element er klikbart og åbner eksterne links sikkert.
          ================================================== */}
      <section className="py-20 bg-[#121619] border-b border-stone-800 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
              SAMARBEJDSPARTNERE
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mt-2">
              Vores stolte netværk af samarbejdspartnere
            </h2>
          </div>
        </div>

        {/* Vandret partnerpræsentation */}
        <div className="relative w-full overflow-hidden">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-[#121619] to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-[#121619] to-transparent z-10" />

          <div className="flex w-max items-center gap-12 sm:gap-20 animate-marquee py-4">
            {[...referencePartners, ...referencePartners].map((partner, idx) => {
              const content = (
                <div className="shrink-0 flex items-center justify-center p-2 group transition-transform duration-200 hover:scale-105">
                  <CmsImage
                    src={partner.img}
                    alt={partner.name}
                    className="h-[75px] sm:h-[90px] max-h-[95px] w-auto object-contain opacity-85 group-hover:opacity-100 transition-opacity duration-200"
                    width={400}
                  />
                </div>
              );

              if (partner.url) {
                return (
                  <a
                    key={idx}
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={partner.name}
                    className="cursor-pointer"
                  >
                    {content}
                  </a>
                );
              }

              return <div key={idx}>{content}</div>;
            })}
          </div>
        </div>
      </section>

      {/* ==================================================
          6. FORSIDE – SPØRGSMÅL OG SVAR
          Overskrift: "Spørgsmål og svar" (på én linje på desktop).
          Billede udskiftet med: pmb-tekniker.webp
          ================================================== */}
      <section className="py-24 lg:py-32 bg-[#121619] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
            {/* Left Column: pmb-tekniker.webp med naturlig beskæring og høj kvalitet */}
            <div className="lg:col-span-5 flex">
              <div className="w-full h-full min-h-[420px] lg:min-h-[580px] relative overflow-hidden rounded-lg bg-stone-900 border border-stone-800">
                <CmsImage
                  src="https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pmb-tekniker.webp"
                  alt="PMB servicetekniker i arbejde"
                  className="w-full h-full object-cover object-center filter brightness-95"
                  width={900}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121619]/60 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Right Column: Content + Accordion */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#0d8086]">
                  Ofte stillede spørgsmål
                </span>
                {/* Overskrift på én samlet linje på desktop */}
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mt-2.5 whitespace-normal sm:whitespace-nowrap">
                  Spørgsmål og svar
                </h2>
                <p className="mt-4 text-stone-400 italic text-base sm:text-lg leading-relaxed">
                  Få svar på de mest almindelige spørgsmål om vores maskiner, centralsmøringsanlæg, tekniske serviceaftaler og garantier.
                </p>
              </div>

              {/* Stacked accordion */}
              <div className="mt-8 sm:mt-10 border-t border-stone-800 divide-y divide-stone-800">
                {(faqs.length > 0 ? faqs : defaultFaqs).map((faq, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div key={faq.id || idx} className="py-1">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                        aria-expanded={isOpen}
                        className="w-full py-5 flex items-center justify-between gap-4 text-left group cursor-pointer"
                      >
                        <span className="text-base sm:text-lg font-bold text-white group-hover:text-[#0d8086] transition-colors">
                          {faq.spoergsmaal}
                        </span>
                        <i
                          className={`fa-solid fa-chevron-down text-sm text-[#0d8086] transition-transform duration-200 shrink-0 ${
                            isOpen ? 'rotate-180 text-white' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="pl-0 sm:pl-4 pb-5 pt-0 text-sm sm:text-base text-stone-300 leading-relaxed animate-in fade-in duration-200">
                          <div
                            dangerouslySetInnerHTML={{ __html: faq.svar }}
                            className="prose prose-invert prose-stone max-w-none text-stone-300 leading-relaxed [&_p]:my-2"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox component */}
      <Lightbox
        images={referencePartners.map((c) => ({ url: c.img, title: c.name }))}
        initialIndex={activeGalleryIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
